import SliderField from "./SliderField";
import { PARAM_RANGES } from "../geometry/defaults";
import "./Sidebar.css";

const GEOMETRY_KEYS = ["sod", "sdd"];

function DeltaBadge({ pct }) {
  const sign = pct > 0 ? "+" : "";
  const color = pct >= 0 ? "#5EEAD4" : "#F97362";
  return (
    <span className="fov-stat-delta" style={{ color }}>
      {sign}
      {pct.toFixed(1)}%
    </span>
  );
}

export default function Sidebar({
  params,
  onParamChange,
  showInclined,
  showCoplanar,
  showGimbal,
  onToggleInclined,
  onToggleCoplanar,
  onToggleGimbal,
  fovResults,
  relativeToCoplanar,
  theta,
  onThetaChange,
  onReset,
  showAllFootprints,
  onToggleAllFootprints,
  showVolume,
  onToggleVolume,
  showXraySurface,
  onToggleXraySurface,
  onFocusFov,
  onFocusOverall,
  onFocusDirection,
  onSuggestVolRes,
}) {
  return (
    <aside className="sidebar">
      <div className="sidebar-fixed">
        <section className="sidebar-section">
          <h3 className="sidebar-heading">Setups</h3>
          <label className="checkbox-row">
            <input type="checkbox" checked={showCoplanar} onChange={onToggleCoplanar} />
            <span className="swatch swatch-coplanar" />
            Coplanar laminography
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={showInclined} onChange={onToggleInclined} />
            <span className="swatch swatch-inclined" />
            Inclined laminography
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={showGimbal} onChange={onToggleGimbal} />
            <span className="swatch swatch-gimbal" />
            Gimbal lock laminography
          </label>
        </section>

        <section className="sidebar-section">
          <h3 className="sidebar-heading">FOV Surface Area Comparison</h3>
          <div className="fov-stat">
            <span className="fov-stat-label">Coplanar</span>
            <span className="fov-stat-value">{fovResults.coplanar.area.toFixed(4)} mm²</span>
            <span className="fov-stat-delta fov-stat-delta-base">base</span>
          </div>
          <div className="fov-stat">
            <span className="fov-stat-label">Inclined</span>
            <span className="fov-stat-value">{fovResults.inclined.area.toFixed(4)} mm²</span>
            <DeltaBadge pct={relativeToCoplanar.inclined} />
          </div>
          <div className="fov-stat">
            <span className="fov-stat-label">Gimbal lock</span>
            <span className="fov-stat-value">{fovResults.gimbal.area.toFixed(4)} mm²</span>
            <DeltaBadge pct={relativeToCoplanar.gimbal} />
          </div>
          <label className="checkbox-row checkbox-row-secondary">
            <input
              type="checkbox"
              checked={showAllFootprints}
              onChange={onToggleAllFootprints}
            />
            Display footprint
          </label>
          <label className="checkbox-row checkbox-row-tight">
            <input
              type="checkbox"
              checked={showXraySurface}
              onChange={onToggleXraySurface}
            />
            Display x-ray surface
          </label>
          <label className="checkbox-row checkbox-row-tight">
            <input
              type="checkbox"
              checked={showVolume}
              onChange={onToggleVolume}
            />
            Display volume
          </label>
          <div className="view-button-row">
            <button type="button" className="view-button" onClick={onFocusFov}>
              FOV
            </button>
            <button type="button" className="view-button" onClick={onFocusOverall}>
              Overall
            </button>
          </div>
          <div className="view-button-grid">
            {["+X", "+Y", "+Z", "-X", "-Y", "-Z"].map((direction) => (
              <button
                key={direction}
                type="button"
                className="view-button"
                onClick={() => onFocusDirection(direction)}
              >
                {direction}
              </button>
            ))}
          </div>
        </section>
      </div>

      <div className="sidebar-scroll">
        <section className="sidebar-section">
          <h3 className="sidebar-heading">Projection</h3>
          
          <SliderField
            label={PARAM_RANGES.numAngles.label}
            unit={PARAM_RANGES.numAngles.unit}
            value={params.numAngles}
            min={PARAM_RANGES.numAngles.min}
            max={PARAM_RANGES.numAngles.max}
            step={PARAM_RANGES.numAngles.step}
            onChange={(v) => onParamChange("numAngles", v)}
          />

          <div className="slider-card-header">
            <span className="slider-card-label">Angle range</span>
            <span className="slider-card-unit">deg</span>
          </div>
          <div className="slider-row">
            <SliderField
              compact
              prefix="Start"
              label=""
              value={params.angleStart}
              min={PARAM_RANGES.angleStart.min}
              max={PARAM_RANGES.angleStart.max}
              step={PARAM_RANGES.angleStart.step}
              onChange={(v) => onParamChange("angleStart", v)}
            />
            <SliderField
              compact
              prefix="End"
              label=""
              value={params.angleEnd}
              min={PARAM_RANGES.angleEnd.min}
              max={PARAM_RANGES.angleEnd.max}
              step={PARAM_RANGES.angleEnd.step}
              onChange={(v) => onParamChange("angleEnd", v)}
            />
          </div>

          <SliderField
            label="Theta"
            unit="deg"
            value={theta}
            min={Math.min(params.angleStart, params.angleEnd)}
            max={Math.max(params.angleStart, params.angleEnd)}
            step={
              params.numAngles > 1
                ? Math.abs(params.angleEnd - params.angleStart) / (params.numAngles - 1)
                : 1
            }
            onChange={onThetaChange}
          />
        </section>

        <section className="sidebar-section">
          <h3 className="sidebar-heading">Geometry</h3>
          {GEOMETRY_KEYS.map((key) => (
            <SliderField
              key={key}
              label={PARAM_RANGES[key].label}
              unit={PARAM_RANGES[key].unit}
              value={params[key]}
              min={PARAM_RANGES[key].min}
              max={PARAM_RANGES[key].max}
              step={PARAM_RANGES[key].step}
              onChange={(v) => onParamChange(key, v)}
            />
          ))}
          <div className="slider-card-header">
            <span className="slider-card-label">Tilt</span>
            <span className="slider-card-unit">{PARAM_RANGES.tiltX.unit}</span>
          </div>
          <div className="slider-row">
            <SliderField
              compact
              prefix="X"
              label=""
              value={params.tiltX}
              min={PARAM_RANGES.tiltX.min}
              max={PARAM_RANGES.tiltX.max}
              step={PARAM_RANGES.tiltX.step}
              onChange={(v) => onParamChange("tiltX", v)}
            />
            <SliderField
              compact
              prefix="Y"
              label=""
              value={params.tiltY}
              min={PARAM_RANGES.tiltY.min}
              max={PARAM_RANGES.tiltY.max}
              step={PARAM_RANGES.tiltY.step}
              onChange={(v) => onParamChange("tiltY", v)}
            />
          </div>
        </section>

        <section className="sidebar-section">
          <h3 className="sidebar-heading">Detector</h3>
          <div className="slider-card-header">
            <span className="slider-card-label">Resolution</span>
            <span className="slider-card-unit">{PARAM_RANGES.detWidth.unit}</span>
          </div>
          <div className="slider-row">
            <SliderField
              compact
              prefix="W"
              label=""
              value={params.detWidth}
              min={PARAM_RANGES.detWidth.min}
              max={PARAM_RANGES.detWidth.max}
              step={PARAM_RANGES.detWidth.step}
              onChange={(v) => onParamChange("detWidth", v)}
            />
            <SliderField
              compact
              prefix="H"
              label=""
              value={params.detHeight}
              min={PARAM_RANGES.detHeight.min}
              max={PARAM_RANGES.detHeight.max}
              step={PARAM_RANGES.detHeight.step}
              onChange={(v) => onParamChange("detHeight", v)}
            />
          </div>

          <SliderField
            label={PARAM_RANGES.detPitch.label}
            unit={PARAM_RANGES.detPitch.unit}
            value={params.detPitch}
            min={PARAM_RANGES.detPitch.min}
            max={PARAM_RANGES.detPitch.max}
            step={PARAM_RANGES.detPitch.step}
            onChange={(v) => onParamChange("detPitch", v)}
          />

          <div className="slider-card-header">
            <span className="slider-card-label">Offset</span>
            <span className="slider-card-unit">{PARAM_RANGES.offsetU.unit}</span>
          </div>
          <div className="slider-row">
            <SliderField
              compact
              prefix="U"
              label=""
              value={params.offsetU}
              min={PARAM_RANGES.offsetU.min}
              max={PARAM_RANGES.offsetU.max}
              step={PARAM_RANGES.offsetU.step}
              onChange={(v) => onParamChange("offsetU", v)}
            />
            <SliderField
              compact
              prefix="V"
              label=""
              value={params.offsetV}
              min={PARAM_RANGES.offsetV.min}
              max={PARAM_RANGES.offsetV.max}
              step={PARAM_RANGES.offsetV.step}
              onChange={(v) => onParamChange("offsetV", v)}
            />
          </div>
        </section>

        {/* <section className="sidebar-section">
          <h3 className="sidebar-heading">Acquisition</h3>
          
        </section> */}

        <section className="sidebar-section">
          <h3 className="sidebar-heading">Volume</h3>
          <div className="slider-card-header">
            <span className="slider-card-label">{PARAM_RANGES.volRes.label}</span>
            <span className="slider-card-unit">{PARAM_RANGES.volRes.unit}</span>
          </div>
          <div className="slider-with-action">
            <SliderField
              compact
              label=""
              value={params.volRes}
              min={PARAM_RANGES.volRes.min}
              max={PARAM_RANGES.volRes.max}
              step={PARAM_RANGES.volRes.step}
              decimalPlaces={6}
              onChange={(v) => onParamChange("volRes", v)}
            />
            <button
              type="button"
              className="icon-button"
              title="Suggest resolution"
              onClick={onSuggestVolRes}
            >
              <svg viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.66519 12C3.00503 12.0004 3.3319 12.1305 3.57901 12.3638C3.82613 12.5971 3.97484 12.9159 3.99475 13.2552C4.01467 13.5944 3.90429 13.9285 3.68617 14.1891C3.46805 14.4497 3.15865 14.6172 2.82119 14.6573L2.66519 14.6667H1.33186C0.992017 14.6663 0.665147 14.5362 0.418032 14.3029C0.170917 14.0696 0.0222095 13.7507 0.00229338 13.4115C-0.0176228 13.0722 0.0927557 12.7382 0.310876 12.4776C0.528996 12.217 0.838395 12.0495 1.17586 12.0093L1.33186 12H2.66519ZM13.3319 0C13.6584 4.30807e-05 13.9736 0.119942 14.2177 0.336954C14.4617 0.553966 14.6176 0.852998 14.6559 1.17733L14.6652 1.33333V2.66667C14.6648 3.00651 14.5347 3.33338 14.3014 3.58049C14.0681 3.82761 13.7493 3.97631 13.41 3.99623C13.0707 4.01615 12.7367 3.90577 12.4761 3.68765C12.2155 3.46953 12.048 3.16013 12.0079 2.82267L11.9985 2.66667V1.33333C11.9985 0.979711 12.139 0.640573 12.389 0.390524C12.6391 0.140476 12.9782 0 13.3319 0ZM25.3319 12C25.6717 12.0004 25.9986 12.1305 26.2457 12.3638C26.4928 12.5971 26.6415 12.9159 26.6614 13.2552C26.6813 13.5944 26.571 13.9285 26.3528 14.1891C26.1347 14.4497 25.8253 14.6172 25.4879 14.6573L25.3319 14.6667H23.9985C23.6587 14.6663 23.3318 14.5362 23.0847 14.3029C22.8376 14.0696 22.6889 13.7507 22.669 13.4115C22.649 13.0722 22.7594 12.7382 22.9775 12.4776C23.1957 12.217 23.5051 12.0495 23.8425 12.0093L23.9985 12H25.3319ZM3.85586 3.85733C4.08545 3.62776 4.39091 3.48986 4.71495 3.46948C5.03898 3.44911 5.35932 3.54766 5.61586 3.74667L5.74119 3.85733L6.67452 4.79067C6.91365 5.03061 7.05249 5.35258 7.06283 5.69118C7.07317 6.02978 6.95424 6.35962 6.73019 6.6137C6.50615 6.86779 6.19379 7.02707 5.85656 7.0592C5.51933 7.09132 5.18251 6.99388 4.91452 6.78667L4.78919 6.676L3.85586 5.74267C3.60589 5.49263 3.46547 5.15355 3.46547 4.8C3.46547 4.44645 3.60589 4.10737 3.85586 3.85733ZM20.9225 3.85733C21.1625 3.6182 21.4844 3.47937 21.823 3.46903C22.1616 3.45869 22.4915 3.57762 22.7456 3.80167C22.9996 4.02571 23.1589 4.33807 23.1911 4.6753C23.2232 5.01253 23.1257 5.34934 22.9185 5.61733L22.8079 5.74267L21.8745 6.676C21.6346 6.91513 21.3126 7.05397 20.974 7.0643C20.6354 7.07464 20.3056 6.95571 20.0515 6.73167C19.7974 6.50762 19.6381 6.19526 19.606 5.85803C19.5739 5.5208 19.6713 5.18399 19.8785 4.916L19.9892 4.79067L20.9225 3.85733ZM15.9985 21.3333C16.3521 21.3333 16.6913 21.4738 16.9413 21.7239C17.1914 21.9739 17.3319 22.313 17.3319 22.6667C17.3319 23.7275 16.9104 24.7449 16.1603 25.4951C15.4101 26.2452 14.3927 26.6667 13.3319 26.6667C12.271 26.6667 11.2536 26.2452 10.5034 25.4951C9.75328 24.7449 9.33186 23.7275 9.33186 22.6667C9.3319 22.3401 9.4518 22.0249 9.66881 21.7808C9.88582 21.5368 10.1849 21.3809 10.5092 21.3427L10.6652 21.3333H15.9985ZM13.3319 5.33333C15.0109 5.33333 16.6474 5.86163 18.0095 6.8434C19.3717 7.82516 20.3904 9.21062 20.9213 10.8035C21.4523 12.3964 21.4686 14.116 20.968 15.7187C20.4673 17.3214 19.4751 18.7259 18.1319 19.7333C17.9481 19.8714 17.7318 19.9596 17.5039 19.9893L17.3319 20H9.33186C9.04336 20 8.76265 19.9064 8.53186 19.7333C7.18861 18.7259 6.19639 17.3214 5.69575 15.7187C5.19511 14.116 5.21143 12.3964 5.74239 10.8035C6.27336 9.21062 7.29205 7.82516 8.65417 6.8434C10.0163 5.86163 11.6528 5.33333 13.3319 5.33333Z" fill="#EFEFEF"/>
              </svg>
            </button>
          </div>
          <div className="slider-card-header">
            <span className="slider-card-label">Voxel Size</span>
            <span className="slider-card-unit">{PARAM_RANGES.volX.unit}</span>
          </div>
          <SliderField
            compact
            prefix="X"
            label=""
            value={params.volX}
            min={PARAM_RANGES.volX.min}
            max={PARAM_RANGES.volX.max}
            step={PARAM_RANGES.volX.step}
            onChange={(v) => onParamChange("volX", v)}
          />
          <SliderField
            compact
            prefix="Y"
            label=""
            value={params.volY}
            min={PARAM_RANGES.volY.min}
            max={PARAM_RANGES.volY.max}
            step={PARAM_RANGES.volY.step}
            onChange={(v) => onParamChange("volY", v)}
          />
          <SliderField
            compact
            prefix="Z"
            label=""
            value={params.volZ}
            min={PARAM_RANGES.volZ.min}
            max={PARAM_RANGES.volZ.max}
            step={PARAM_RANGES.volZ.step}
            onChange={(v) => onParamChange("volZ", v)}
          />

          <div className="slider-card-header">
            <span className="slider-card-label">Offset</span>
            <span className="slider-card-unit">{PARAM_RANGES.offsetVolX.unit}</span>
          </div>
          <SliderField
            compact
            prefix="X"
            label=""
            value={params.offsetVolX}
            min={PARAM_RANGES.offsetVolX.min}
            max={PARAM_RANGES.offsetVolX.max}
            step={PARAM_RANGES.offsetVolX.step}
            decimalPlaces={3}
            dragSensitivity={0.1}
            onChange={(v) => onParamChange("offsetVolX", v)}
          />
          <SliderField
            compact
            prefix="Y"
            label=""
            value={params.offsetVolY}
            min={PARAM_RANGES.offsetVolY.min}
            max={PARAM_RANGES.offsetVolY.max}
            step={PARAM_RANGES.offsetVolY.step}
            decimalPlaces={3}
            dragSensitivity={0.1}
            onChange={(v) => onParamChange("offsetVolY", v)}
          />
          <SliderField
            compact
            prefix="Z"
            label=""
            value={params.offsetVolZ}
            min={PARAM_RANGES.offsetVolZ.min}
            max={PARAM_RANGES.offsetVolZ.max}
            step={PARAM_RANGES.offsetVolZ.step}
            decimalPlaces={3}
            dragSensitivity={0.1}
            onChange={(v) => onParamChange("offsetVolZ", v)}
          />
        </section>

        <button type="button" className="reset-button" onClick={onReset}>
          Reset
        </button>
      </div>
    </aside>
  );
}
